
import { defineComponent } from 'vue';
import BigCardContent from './bigCardContent.vue';
import DiscoverContent from './discoverContent.vue';
import { getMetaInfo, header, extractTitleFromMetaInfo, getDefaultTitle, getLdJson } from '@/common/seo';
import { setCommonParamsCookiesInServer } from '@/common/common-params';
import { DiscoverApi } from '~/api/discover';
import { RequestSource, ResponseBaseResult, WebFeedType } from '~/api/base';
import { Feed } from '~/api/feed';

const ForbidWordRegExp =
    /[\u4e00-\u9fa5]|(https?:\/\/.*)|(\.(com|net|org|co|io|gov|edu|biz|info|me|mil|tv|name|cc|app|dev|cloud|shop|site|tech|online|pro|win|cn))/i;

export default defineComponent<any>({
    async asyncData(ctx) {
        let seoData = [];
        let metaInfoData = [];
        let title = getDefaultTitle(ctx);
        const pidStrList = [];
        let feedsListOrigin: Array<Feed> = [];
        let cursor: number | string = 0;

        if (process.server) {
            try {
                const isForbidWord = ForbidWordRegExp.test(ctx.route.params.content || '');

                if (isForbidWord) {
                    ctx.redirect(301, '/foryou');
                    return;
                }

                setCommonParamsCookiesInServer({
                    bucket: ctx.store.state.startup.bucket,
                    host: ctx.req.headers.host,
                    ctx,
                    countryInfo: ctx.store.state.startup.countryInfo,
                });

                // const discoverNewUIAbRes = await ctx.$axios.$$post('overseaAPI::/rest/o/w/seo/ab/getByUrl', {
                //     url: ctx?.route?.path,
                //     parameter: 'seo_new_discover',
                // });

                // ctx.store.commit('setNewDiscoverExpValue', discoverNewUIAbRes?.data?.value || '');
                // const isDiscoverNewUI = ctx.store?.state?.newDiscoverExpValue === 'true';

                /**
                 * @zh app 工厂需求，这里推全了代码后续下掉，本期写死 true
                 * @zh TODO @jinyao
                 */
                const isDiscoverNewUI = true;

                // 命中新老ui，分别请求不同的接口
                if (isDiscoverNewUI) {
                    const content = ctx?.route?.params?.content || '';
                    const response = await DiscoverApi(ctx?.$axios).getDiscoverList({
                        searchWord: encodeURIComponent(content?.trim()?.replace(/\s/g, '-')),
                        pcursor: 0,
                        count: 8,
                        fromUser: false,
                        mobile: true,
                        request_source: RequestSource.REQUEST_SOURCE_PWA_SEO_DISCOVER,
                    });
                    if (response?.result === ResponseBaseResult.RESULT_OK) {
                        feedsListOrigin = response?.feeds || [];
                        cursor = response?.pcursor || '';

                        if (response?.feeds?.length) {
                            for (let i = 0; i < response?.feeds?.length; i++) {
                                pidStrList.push(response.feeds?.[i]?.photo_id_str || '');
                            }
                        }
                    }
                } else {
                    const newDiscoverRes = await ctx.store.dispatch('newDiscoverModule/getNewDiscoverList', ctx);
                    if (newDiscoverRes.result === 1) {
                        ctx.store.commit('newDiscoverModule/setNewDiscoverList', { data: newDiscoverRes.data });

                        if (newDiscoverRes?.data?.length) {
                            for (let i = 0; i < newDiscoverRes.data.length; i++) {
                                const item = newDiscoverRes.data[i];
                                const {
                                    type,
                                    data: { feeds },
                                } = item;
                                if (type === 'video-category-card') {
                                    pidStrList?.push(feeds?.photo_id_str || '');
                                } else if (type === 'picture-category-card') {
                                    pidStrList?.push(feeds?.pic_id_str || '');
                                } else if (type === 'user-video-card') {
                                    const userVideoList = feeds?.map((userVideoItem: any) => userVideoItem?.photo_id_str || '')?.slice(0, 2) || [];
                                    pidStrList?.push(...userVideoList);
                                }
                            }
                        }
                    }
                }

                // 处理metaInfo和结构化数据
                const url = `https://${ctx?.req?.headers?.host}${ctx?.route?.path}` || '';
                const seoPromise = getLdJson({ ctx, params: { url, pidStrList: pidStrList || '' } });
                const metaInfoPromise = getMetaInfo({
                    ctx,
                    params: {
                        url,
                        pidStrList: pidStrList || '',
                        locale: ctx.req.headers['accept-language'],
                    },
                });
                const [seoRes, metaInfoRes] = await Promise.all([seoPromise, metaInfoPromise]);
                if (seoRes.status === 200) {
                    seoData = seoRes.data;
                }
                if (metaInfoRes.status === 200) {
                    metaInfoData = metaInfoRes.data;
                    title = extractTitleFromMetaInfo({ ctx, metaInfoData });
                }
            } catch (error) {
                console.log(error);
            }
        }

        return {
            seoData,
            metaInfoData,
            title,
            cursor,
            feedsListOrigin,
        };
    },
    head() {
        const head = header(this) as any;

        /**
         * @zh app 工厂需求，这里推全了代码后续下掉，本期写死 true
         * @zh TODO @jinyao
         */
        // const isDiscoverNewUI = this.$store?.state?.newDiscoverExpValue === 'true';
        const isDiscoverNewUI = true;

        if (isDiscoverNewUI) {
            const firstItem: Feed = this.feedsListOrigin?.[0];
            if (firstItem?.web_feed_type === WebFeedType.PHOTO_CARD) {
                head.link.push({
                    rel: 'preload',
                    href: firstItem?.cover_first_frame_urls?.[0]?.url,
                    as: 'image',
                });

                head.script.push({
                    innerHTML: `
                        if (window && !window._preloadVideo) {
                            window._preloadVideo = document.createElement('video');
                            window._preloadVideo.src = '${firstItem?.main_mv_urls?.[0]?.url}';
                            window._preloadVideo.preload = 'auto';
                            window._preloadVideo.load();
                        }
                    `,
                });
            }
        } else if (this.$store.state.startup.discoverUi === 'old_ui') {
            const imgSrcArr: Array<string> = [];

            this.$store.state.discoverFeeds.forEach((item: any) => {
                const src = item.cover_first_frame_urls && item.cover_first_frame_urls[0]?.url;
                if (src) {
                    imgSrcArr.push(src);
                }
            });

            imgSrcArr.forEach(imgSrc => {
                head.link.push({
                    rel: 'preload',
                    href: imgSrc,
                    as: 'image',
                });
            });
        } else {
            // 预先加载的img标签
            const firstItem = this.$store.state?.newDiscoverModule?.newDiscoverList?.[0];
            // 把第一个预加载掉
            if (firstItem?.type === 'video-category-card') {
                head.link.push({
                    rel: 'preload',
                    href: firstItem.data?.feeds?.cover_thumbnail_urls?.[0]?.url,
                    as: 'image',
                });
                // head.link.push({
                //   rel: 'preload',
                //   href: firstItem.data?.feeds?.main_mv_urls?.[0]?.url,
                //   as: "video",
                //   type: "video/mp4"
                // })

                head.script.push({
                    innerHTML: `
                    const _preloadVideo = document.createElement('video');
                    _preloadVideo.src = '${firstItem.data?.feeds?.main_mv_urls?.[0]?.url}';
                    _preloadVideo.preload = 'auto';
                    _preloadVideo.load();
                `,
                });
            }

            if (firstItem?.type === 'user-video-card') {
                firstItem.data.feeds.slice(0, 2).forEach((item: any) => {
                    head.link.push({
                        rel: 'preload',
                        href: item?.cover_thumbnail_urls?.[0]?.url,
                        as: 'image',
                    });
                    head.link.push({
                        rel: 'preload',
                        href: item?.main_mv_urls?.[0]?.url,
                        as: 'video',
                        type: 'video/mp4',
                    });
                });
            }
        }

        return {
            ...head,
        };
    },
    render(c) {
        /**
         * @zh app 工厂需求，这里推全了代码后续下掉，本期写死 true
         * @zh TODO @jinyao
         */
        // const isDiscoverNewUI = this.$store?.state?.newDiscoverExpValue === 'true';
        const isDiscoverNewUI = true;
        const comp: any = isDiscoverNewUI ? DiscoverContent : BigCardContent;

        return c(comp, {
            props: {
                seoData: this.seoData,
                metaInfoData: this.metaInfoData,
                title: this.title,
                feedsListOrigin: this.feedsListOrigin,
                cursor: this.cursor,
            },
        });
    },
});
